import type { Dispatch, SetStateAction } from 'react'

import { useCallback, useEffect, useRef, useState } from 'react'

export type UseSearchAutocompleteListNavigationParams = {
  onClose: () => void
  autocompleteItemCount?: number
}

export type UseSearchAutocompleteListNavigationResult = {
  highlightedIndex: number | null
  setHighlightedIndex: Dispatch<SetStateAction<number | null>>
  setPick: (index: number, pick: (() => void) | null) => void
  stableOnClose: () => void
}

/**
 * Keyboard navigation for the search autocomplete listbox while focus stays in the text input.
 *
 * ## Why `window` + capture phase
 * The menu is a sibling of the input in the DOM (not an ancestor), so `keydown` from the input does
 * not bubble through the listbox container. A document-level listener in the capture phase runs
 * before the focused control handles the event, so arrows / Enter / Escape still work during typing.
 *
 * ## Picks map (`picksRef` + `setPick`)
 * Each `SearchAutocompleteItem` registers its skin-bound `onPick` under its row index (`useLayoutEffect`).
 * On Enter we look up the callback for `highlightedIndex` so the menu does not need the suggestions
 * array or duplicated pick logic — only the count must match the skin `map` length.
 */
export function useSearchAutocompleteListNavigation({
  onClose,
  autocompleteItemCount,
}: UseSearchAutocompleteListNavigationParams): UseSearchAutocompleteListNavigationResult {
  // index -> onPick from each item (registered by SearchAutocompleteItem)
  const picksRef = useRef<Map<number, () => void>>(new Map())
  const onCloseRef = useRef(onClose)
  onCloseRef.current = onClose

  const stableOnClose = useCallback(() => {
    onCloseRef.current()
  }, [])

  const [highlightedIndex, setHighlightedIndex] = useState<number | null>(null)
  const highlightedIndexRef = useRef<number | null>(null)
  highlightedIndexRef.current = highlightedIndex

  const setPick = useCallback((index: number, pick: (() => void) | null) => {
    if (pick == null) {
      picksRef.current.delete(index)
    } else {
      picksRef.current.set(index, pick)
    }
  }, [])

  const itemCount = autocompleteItemCount ?? 0

  useEffect(() => {
    if (itemCount === 0) return

    const onKeyDown = (e: KeyboardEvent): void => {
      if (itemCount === 0) return

      if (e.key === 'Escape') {
        e.preventDefault()
        onCloseRef.current()
        return
      }

      if (e.key === 'ArrowDown') {
        e.preventDefault()
        setHighlightedIndex((prev) => {
          if (prev === null) return 0
          return prev >= itemCount - 1 ? 0 : prev + 1
        })
        return
      }

      if (e.key === 'ArrowUp') {
        e.preventDefault()
        setHighlightedIndex((prev) => {
          if (prev === null) return itemCount - 1
          return prev <= 0 ? itemCount - 1 : prev - 1
        })
        return
      }

      // Delegate pick to the item’s registered handler
      if (e.key === 'Enter') {
        const hi = highlightedIndexRef.current
        const pick = hi !== null ? picksRef.current.get(hi) : undefined
        if (pick) {
          e.preventDefault()
          pick()
        }
      }
    }

    window.addEventListener('keydown', onKeyDown, true)
    return () => window.removeEventListener('keydown', onKeyDown, true)
  }, [itemCount, setHighlightedIndex])

  return {
    highlightedIndex,
    setHighlightedIndex,
    setPick,
    stableOnClose,
  }
}
